/* 五个 Tool 页正文区块，每页约 800 英文词 */

export const viewerSections = [
  {
    h2: 'What Is an Instagram Viewer?',
    paragraphs: [
      'An Instagram viewer lets you preview supported public Instagram media in your browser when you have a direct link to a post or Reel. SnapInsta is built for that narrow job: show what Instagram still exposes for the URL you paste, without asking you to log in and without claiming access to private profiles.',
      'This is different from a full Instagram client. You will not scroll a home feed, read DMs, or browse every photo on someone’s grid from a username alone. You bring a link; the tool tries to load the public media tied to that link.',
      'Availability depends on Instagram’s current rules. A link that worked last month may fail today if the post was deleted, made private, or restricted. The viewer is honest about those limits instead of promising access to everything on Instagram.',
    ],
  },
  {
    h2: 'What You Can View',
    cards: [
      { title: 'Public posts', text: 'Single-image posts and many carousel slides when Instagram returns multiple items for one URL.' },
      { title: 'Public Reels', text: 'Short videos shared with a standard instagram.com/reel/ link while the Reel stays public.' },
      { title: 'Captions in preview', text: 'When metadata is returned with the media, you can confirm you opened the correct post before saving elsewhere.' },
      { title: 'Legacy /tv/ links', text: 'Some older long-form URLs still redirect to playable public video; others may be dead after the IGTV merge.' },
    ],
    paragraphs: [
      'If you need to save files to your device, use the dedicated download tools linked at the bottom of this page. The viewer prioritizes quick preview over bulk downloading.',
    ],
  },
  {
    h2: 'How to Use the Instagram Viewer',
    steps: [
      'Open the public post or Reel in Instagram and tap <strong>Share → Copy link</strong>.',
      'Paste the URL into the box at the top of this page and click <strong>View</strong>.',
      'Wait for the preview to load. Use <strong>Open media</strong> if you want the file in a new browser tab.',
      'If you only have a username, open <strong>instagram.com/username</strong> in your browser for profile browsing; this tool still needs a post or Reel URL.',
    ],
    paragraphs: [
      'Double-check that you copied a post link, not a profile, hashtag, or shop page. Wrong URL types are the most common reason a viewer shows an error even when the content is public in the app.',
    ],
  },
  {
    h2: 'When an Instagram Viewer Is Useful',
    list: [
      'Checking a public post on a laptop before you repost or cite it elsewhere.',
      'Showing a clip to someone who does not use Instagram.',
      'Confirming which carousel slide a shared link points to.',
      'Reviewing a Reel without installing the app on a work device.',
    ],
    paragraphs: [
      'Teams sometimes use a viewer link internally to agree on creative references. It is not a substitute for rights clearance—public viewing does not automatically mean you may reuse the content commercially.',
    ],
  },
  {
    h2: 'What You Cannot View',
    list: [
      'Private accounts and follower-only posts.',
      'Expired Stories unless you still have a working Story URL.',
      'Deleted or restricted media.',
      'Full profile grids from typing a username only.',
    ],
    paragraphs: [
      'SnapInsta does not bypass blocks, age gates, or login walls. If Instagram requires an signed-in session to see the media, this page will not show it either.',
    ],
  },
  {
    h2: 'Instagram Viewer vs Instagram App',
    table: {
      headers: ['Feature', 'SnapInsta viewer', 'Instagram app'],
      rows: [
        ['Preview from a pasted link', 'Yes, when supported', 'Yes'],
        ['Private content', 'No', 'Only with permission'],
        ['Feed, DMs, notifications', 'No', 'Yes'],
        ['Download to device', 'Use <a href="/instagram-photo-downloader/">Photo</a> or <a href="/instagram-reels-downloader/">Reels</a> tools', 'In-app save where allowed'],
        ['Affiliated with Meta', 'No', 'Yes'],
      ],
    },
  },
  {
    h2: 'Viewer Tips on Desktop and Mobile',
    paragraphs: [
      'On desktop, keep the browser tab active while media loads. Some networks block autoplay or large CDN requests until you interact with the page.',
      'On mobile, switching from the Instagram app to your browser clears clipboard data on some devices—paste immediately after copying the link.',
      'If preview fails once, open the same URL in a private window to rule out extensions blocking Instagram CDN domains. If it still fails, the post is likely restricted or removed.',
    ],
  },
  {
    h2: 'Privacy, Safety, and Responsible Use',
    paragraphs: [
      'SnapInsta does not ask for your Instagram password. If any site requests login credentials to “unlock” viewing, treat that as a red flag and leave the page.',
      'Public preview does not grant republication rights. Designers and marketers should still confirm licensing before using someone else’s photo or clip in campaigns, decks, or client deliverables.',
      'If you report content to Instagram for policy violations, use official Meta channels. This viewer is a utility for opening supported URLs, not a moderation or reporting tool.',
      'When sharing a preview link internally, remember that URLs can expose which post you opened. Use the same discretion you would when forwarding an Instagram link in email or chat.',
    ],
  },
  {
    h2: 'Choosing Between View and Download',
    paragraphs: [
      'Use the viewer when you only need to confirm content once—reviewing a meme, checking a product photo, or validating a client reference before a meeting.',
      'Switch to the photo or Reels downloader when you need a file on disk for offline editing, printing, or archiving in a shared drive.',
      'Both flows start with the same copied URL. Picking the right tool saves time and avoids downloading files you never open.',
    ],
  },
];

export const photoDownloaderSections = [
  {
    h2: 'What Is an Instagram Photo Downloader?',
    paragraphs: [
      'This page is for saving still images from supported public Instagram posts. Paste a post URL, preview what Instagram returns, then download the image file to your phone or computer.',
      'You receive the version Instagram serves for that link—not a guaranteed copy of the creator’s original camera file. Many uploads are compressed for faster loading, and a downloader cannot invent detail that was removed upstream.',
      'SnapInsta works in the browser. You do not need an Instagram account for public URLs that Instagram still exposes. Private posts remain off limits.',
    ],
  },
  {
    h2: 'What Can You Download?',
    list: [
      'Single-photo public posts.',
      'Individual images from public carousel posts when multiple files are returned.',
      'Preview thumbnails for some mixed posts where Instagram exposes stills alongside video.',
    ],
    paragraphs: [
      'If a post is video-only, you may see a thumbnail rather than a full gallery. For motion content, try the home downloader or the <a href="/instagram-reels-downloader/">Reels tool</a> with the correct link type.',
    ],
  },
  {
    h2: 'How to Download Instagram Photos',
    steps: [
      'Open the photo post in Instagram (app or web).',
      'Tap <strong>Share</strong>, then <strong>Copy link</strong>.',
      'Paste the link into the downloader box above and click <strong>Download</strong>.',
      'If several images appear, pick the slide you need and save that file.',
      'On mobile, check Downloads or Files if the image does not show in Photos immediately.',
    ],
    paragraphs: [
      'Using the post URL is faster than screenshotting. Screenshots capture UI overlays and screen resolution limits; a download uses the image file Instagram provides when available.',
    ],
  },
  {
    h2: 'Download Photos From Instagram Carousels',
    paragraphs: [
      'Carousel posts bundle multiple photos or videos under one link. Copy the carousel post URL once— not a separate link per slide.',
      'After parsing, SnapInsta lists each available item. Save only the slides you need instead of capturing every screen manually.',
      'If one image appears when you expected several, re-copy the link from the original carousel post. Profile links, hashtag pages, and reshares sometimes point to the wrong object. Our <a href="/how-to-download-all-photos-from-instagram-carousel/">carousel guide</a> walks through common fixes.',
    ],
  },
  {
    h2: 'Instagram Photo Quality',
    paragraphs: [
      'Instagram optimizes images for mobile networks. That often means smaller files than the creator’s original upload. Downloaders cannot reverse compression or upscale missing data.',
      'Compare file size and dimensions after download if quality matters for print or design work. When Instagram only exposes a medium-resolution file, that is the practical ceiling for that link.',
      'For tips on blurry saves, see <a href="/why-is-my-instagram-download-blurry/">why downloads look soft</a> and how to grab the best available version.',
    ],
  },
  {
    h2: 'Download Instagram Photos on Phone',
    paragraphs: [
      '<strong>Android:</strong> Copy the link in Instagram → paste here in Chrome → download → open Downloads or Gallery.',
      '<strong>iPhone:</strong> Copy link → paste in Safari → download → move from Files to Photos if needed. Safari and Chrome store files in different places; our <a href="/where-do-instagram-downloads-go-on-iphone/">iPhone downloads guide</a> explains where to look.',
      'Whichever device you use, keep the Instagram app open long enough to copy a fresh link if an older URL stops working.',
    ],
  },
  {
    h2: 'Why an Instagram Photo May Not Download',
    list: [
      'The account or post is private.',
      'The post was deleted or the URL is incorrect.',
      'The link is a Story, Reel, or profile page rather than a photo post.',
      'Instagram temporarily changed how that media is served—try again later.',
    ],
    paragraphs: [
      'Respect copyright and permission when saving someone else’s work. Downloading for personal reference is different from republishing on your own account or in ads.',
    ],
  },
  {
    h2: 'Desktop Downloads and File Names',
    paragraphs: [
      'On Windows or macOS, browsers usually save to the Downloads folder with a generic file name. Rename files right away if you manage many references for work.',
      'If your browser asks where to save each file, pick a folder you sync or back up. Instagram links do not embed the creator’s username in the file name automatically.',
      'Antivirus or corporate proxies occasionally scan large image downloads slowly—wait for the save to finish before opening the file.',
    ],
  },
  {
    h2: 'When Screenshots Are Not Enough',
    paragraphs: [
      'Screenshots capture whatever is on your screen, including compression and UI chrome. They are fine for quick notes but poor for design references or print.',
      'Downloading from the post link gives you a discrete image file when Instagram exposes one—often cleaner than a cropped screen capture, especially on carousel posts with multiple slides.',
      'If you only need to remember a post, Instagram’s saved collections work inside the app. Use this downloader when you need a file outside Instagram—for example in a mood board folder or offline presentation.',
    ],
  },
  {
    h2: 'Organizing Downloads for Work or School',
    paragraphs: [
      'Rename files right after saving so weeks later you still know which campaign or assignment they belonged to.',
      'Folder naming—by client, date, or project—beats dumping every JPEG into Downloads with generic names.',
      'If you collaborate in Slack or Teams, upload the saved file there instead of re-sharing the Instagram link when the post might be deleted later.',
    ],
  },
  {
    h2: 'Link Types That Often Fail',
    paragraphs: [
      'Profile URLs, explore pages, and hashtag feeds look like Instagram links but do not point to one photo post.',
      'When in doubt, open the post, tap the username to confirm you are on a single post, then copy link again from the ••• or Share menu.',
    ],
  },
];

export const storyDownloaderSections = [
  {
    h2: 'What This Story Downloader Handles',
    paragraphs: [
      'Instagram Stories are designed to disappear from the profile ring after about twenty-four hours unless the owner saves them to Highlights or their archive. This tool helps when you have a direct URL to Story media that Instagram still treats as fetchable public content.',
      'You need a working link—usually copied while the Story is live or from a Highlight item that exposes a share URL. The downloader cannot recover Stories that already expired without a saved copy somewhere on Instagram’s servers.',
      'SnapInsta does not bypass privacy. If you cannot view the Story while logged into Instagram with your normal account, you cannot fetch it here either.',
    ],
  },
  {
    h2: 'How to Download an Instagram Story',
    steps: [
      'While the Story is visible, open it and tap <strong>Share → Copy link</strong> when that option appears.',
      'Paste the URL into the field above immediately—waiting increases the chance the link dies.',
      'Click <strong>Download</strong> and save the photo or video file shown.',
      'For your own Story, also consider Instagram’s archive or Highlights so you keep a copy inside the app.',
    ],
    paragraphs: [
      'If you are saving someone else’s Story, ask before reposting. A file on your camera roll is easy to share accidentally; permission matters as much as the technical steps.',
    ],
  },
  {
    h2: 'Story Photos vs Story Videos',
    paragraphs: [
      'Stories may be still images or short clips with or without music. SnapInsta displays whichever format Instagram returns for your URL.',
      'Video Stories sometimes include audio in the downloaded file when Instagram serves a full video stream. Licensed music may be muted or altered outside the app—do not assume the downloaded clip matches what you heard inside Instagram.',
      'Stickers, polls, and interactive overlays might not appear exactly as they did in the Story viewer; you are downloading the underlying media Instagram exposes, not a screen recording of the UI.',
    ],
  },
  {
    h2: 'Highlights and Expired Stories',
    paragraphs: [
      'Highlights keep selected Stories on a profile beyond the one-day limit. If a moment was added to a Highlight, open that Highlight and copy a link from the specific item when Instagram offers one.',
      'Once a Story expires without being saved, viewers generally lose access. No third-party tool can reliably pull expired content that Instagram no longer hosts publicly.',
      'Plan ahead for Stories you know you will need later—creators should archive internally; viewers should copy links promptly or ask the owner to Highlight the clip.',
    ],
  },
  {
    h2: 'Common Story Download Problems',
    list: [
      'The twenty-four hour window passed and the Story was not saved.',
      'The account is private or you are not an approved viewer.',
      'You pasted a profile URL instead of the Story link.',
      'Instagram disabled link sharing for that Story.',
      'You waited too long and the URL expired.',
    ],
    paragraphs: [
      'For permanent public video, Reels and feed posts behave differently. Use the <a href="/instagram-reels-downloader/">Reels downloader</a> for instagram.com/reel/ URLs, not this page.',
      'For a deeper walkthrough, read <a href="/how-to-download-instagram-stories-before-they-disappear/">how to download Stories before they disappear</a>.',
    ],
  },
  {
    h2: 'Stories vs Highlights vs Archive',
    paragraphs: [
      'Your own Stories land in Instagram’s archive automatically when that setting is on—check there before relying on an external downloader.',
      'Highlights are visible on the profile longer, but not every Highlight offers a share link. When link sharing is off, in-app viewing is the only option.',
      'Use third-party saves as a convenience backup, not the primary record for compliance or client approvals.',
    ],
  },
  {
    h2: 'Timing Matters for Story Links',
    paragraphs: [
      'Copy the link while you are still viewing the Story. Waiting until the ring advances several times increases the chance Instagram rotates share tokens.',
      'If a friend sends you a Story link in chat, open it promptly. Chat apps may preview the URL, but the underlying Story can still expire independently of the message.',
      'For recurring saves—such as weekly updates from a public brand account—consider following the account and using Highlights they maintain instead of chasing ephemeral links every day.',
    ],
  },
  {
    h2: 'Story Downloads on Phone vs Desktop',
    paragraphs: [
      'Most people capture Story links on phones. Paste into mobile Safari or Chrome, download, then move the file to Photos or Gallery if your workflow requires it.',
      'Desktop works when someone emails you a Story URL. The same downloader field accepts links copied from Instagram Web when sharing is enabled there.',
      'Large video Stories may take longer on cellular data. Wi‑Fi reduces failed downloads caused by switching apps mid-transfer.',
    ],
  },
  {
    h2: 'Respect Creators When Saving Stories',
    paragraphs: [
      'A downloaded Story file is easy to forward. Ask the creator before reposting their clip to your own audience or using it in ads.',
      'SnapInsta is not affiliated with Instagram or Meta. Follow their terms when saving content you did not create.',
    ],
  },
  {
    h2: 'Notifications, Mentions, and Shared Stories',
    paragraphs: [
      'When someone mentions you in a Story, the notification may deep-link into Instagram rather than giving you a long-lived share URL.',
      'If you need a file copy for records, copy the link while the Story is still live or ask the creator to Highlight it.',
      'Screenshots of mention stickers do not replace permission—talk to the creator before reusing their clip in your own Story or feed.',
    ],
  },
];

export const reelsDownloaderSections = [
  {
    h2: 'What Is an Instagram Reels Downloader?',
    paragraphs: [
      'Reels are short vertical videos meant for discovery and sharing. This page downloads public Reels when you paste a link that contains instagram.com/reel/ or /reels/ with a valid short code.',
      'Unlike Stories, many Reels stay on a profile like normal posts. That makes them easier to link back to—but music licensing still affects what audio appears in a downloaded file.',
      'SnapInsta previews the clip first so you confirm you grabbed the correct URL before saving storage space on your device.',
    ],
  },
  {
    h2: 'What You Need Before You Start',
    list: [
      'A stable internet connection.',
      'The Reel link from Instagram’s share menu—not a profile, hashtag, or explore page URL.',
      'Confirmation that the Reel is still public in the app.',
    ],
    paragraphs: [
      'If the creator deleted the Reel or restricted it to followers, parsing will fail. Copy a fresh link after reopening the Reel to avoid stale URLs from your clipboard history.',
    ],
  },
  {
    h2: 'How to Download an Instagram Reel',
    steps: [
      'Open the Reel in Instagram.',
      'Tap the three-dot menu or <strong>Share</strong>, then <strong>Copy link</strong>.',
      'Paste the URL above and click <strong>Download</strong>.',
      'Save the video file from the results list.',
      'Optional: preview in the <a href="/instagram-viewer/">Instagram viewer</a> first if you only need to watch once.',
    ],
    paragraphs: [
      'Desktop users can paste the same link in a browser. Mobile users often switch between the Instagram app and Chrome or Safari to complete the download.',
    ],
  },
  {
    h2: 'Reels on Android and iPhone',
    paragraphs: [
      'On Android, downloaded files usually land in the Downloads folder unless your browser is set to save elsewhere. Open the file from the notification shade or Files app.',
      'On iPhone, Safari may place videos in Downloads inside the Files app before you move them to Photos. Give the download time to finish on slow networks—large clips can take a minute.',
      'If storage is low, free space before retrying. Failed downloads sometimes leave partial files that block a second attempt until you clear them.',
    ],
  },
  {
    h2: 'Audio in Downloaded Reels',
    paragraphs: [
      'Reels often use commercial music cleared for playback inside Instagram only. Exported files may include full audio, partial audio, or muted sections depending on region and rights.',
      'SnapInsta does not guarantee a particular song or sound effect. Treat audio as a bonus, not a promise, when reusing clips outside Instagram.',
      'If you plan to republish, use royalty-free audio you control or get explicit permission from the original creator.',
    ],
  },
  {
    h2: 'Why a Reel Download May Fail',
    list: [
      'The Reel was deleted or made private.',
      'You copied a feed photo link instead of a Reel URL.',
      'Music or regional restrictions changed the stream.',
      'Temporary Instagram outages—retry with a new copy of the link.',
    ],
    paragraphs: [
      'Reels differ from Stories in lifespan. For disappearing links, use the <a href="/instagram-story-downloader/">Story downloader</a>. For step-by-step mobile help, see <a href="/how-to-download-instagram-reels-to-your-phone/">download Reels to your phone</a>.',
    ],
  },
  {
    h2: 'Editing and Reposting Downloaded Reels',
    paragraphs: [
      'Trimming a downloaded Reel in another app does not remove copyright obligations. You still need rights to the footage and to any music in the clip.',
      'Many brands prefer linking to the original Reel on Instagram instead of re-uploading the MP4, so analytics and credit stay with the creator.',
      'If you edit for accessibility—captions, translations, or contrast—keep a note of the source URL for transparency.',
    ],
  },
  {
    h2: 'Reels Quality and File Size',
    paragraphs: [
      'Downloaded Reels reflect the stream Instagram serves, not always the highest bitrate you see while scrolling. File size varies with length, music, and effects.',
      'If a saved clip looks soft, compare it to playback inside Instagram on the same network. Some softness comes from source upload quality rather than the downloader.',
      'For editing workflows, import the downloaded file into your editor and check resolution before scaling. Upscaling cannot recover detail that was never in the file.',
    ],
  },
  {
    h2: 'Sharing Downloaded Reels Outside Instagram',
    paragraphs: [
      'Messaging apps compress video again when you forward files. Send the original download when possible, or link back to the public Reel instead of re-encoding.',
      'Educators and trainers sometimes download public Reels for offline classrooms. Verify that use fits copyright and platform policies for your institution.',
      'When crediting creators, name the account and link to the Reel when you publish commentary—not only the downloaded file without context.',
    ],
  },
  {
    h2: 'Quick Checklist Before You Click Download',
    paragraphs: [
      'Confirm the URL contains /reel/ or /reels/. Feed photo links will not return video.',
      'Replay the Reel once in Instagram to verify it still plays, then copy a fresh link.',
      'Pick a folder on your device before downloading long clips so you know where the file landed.',
    ],
  },
  {
    h2: 'Batch Saves and Repeat Downloads',
    paragraphs: [
      'SnapInsta processes one URL at a time. For several Reels, copy each link separately and download in sequence.',
      'Pause briefly between downloads on slow networks so the browser finishes writing the previous file.',
      'Keep a simple spreadsheet of Reel URLs and download dates if you curate reference libraries for editing teams.',
    ],
  },
  {
    h2: 'Vertical Video in Other Apps',
    paragraphs: [
      'Reels download as vertical MP4 files. Most editors import them correctly; rotate only if you intentionally deliver landscape.',
      'Preview audio in your editor before publishing elsewhere—Instagram-licensed tracks may be silent outside the app.',
    ],
  },
];

export const igtvDownloaderSections = [
  {
    h2: 'About IGTV and Instagram Video Today',
    paragraphs: [
      'IGTV was Instagram’s long-form vertical video product with its own tab and standalone app. In July 2021 Meta announced it was merging IGTV with in-feed video into a format called Instagram Video.',
      'The IGTV name still appears in old bookmarks, emails, and search queries, but it is no longer a separate active product inside the app. This page exists for those legacy links—not for uploading new “IGTV episodes” today.',
      'When a /tv/ URL still resolves to public media, SnapInsta can try to download it using the same parsing flow as other Instagram posts. When the redirect is dead, no tool can recover the file.',
    ],
  },
  {
    h2: 'What Links Can Work',
    list: [
      'Legacy <strong>instagram.com/tv/…</strong> URLs that redirect to playable video.',
      'Some migrated long videos now living as standard profile or feed video posts.',
      'Public links shared before the IGTV rebrand that Instagram has not retired.',
    ],
    paragraphs: [
      'For content published after the merge, creators typically share normal post or Reel links. Use the <a href="/instagram-reels-downloader/">Reels downloader</a> or the home SnapInsta tool instead of expecting a /tv/ path.',
    ],
  },
  {
    h2: 'How to Use This IGTV Downloader',
    steps: [
      'Find the old IGTV or /tv/ link in your bookmarks, messages, or notes.',
      'Paste it into the downloader field above.',
      'Click <strong>Download</strong> when preview media appears.',
      'If parsing fails, search the creator’s profile for a reposted version or accept that the file may be gone.',
    ],
    paragraphs: [
      'Keep a local backup if you manage a catalog of older videos. Relying on decade-old URLs as your only archive is risky as Instagram continues to simplify video products.',
    ],
  },
  {
    h2: 'Why an Old IGTV Link May Fail',
    list: [
      'The creator deleted the video during the platform transition.',
      'The account became private or restricted.',
      'Instagram removed the redirect from /tv/ to the new location.',
      'The URL pointed to a channel page, not a single video.',
    ],
    paragraphs: [
      'Error messages here usually mean Instagram no longer serves public media for that address—not that your device is broken.',
    ],
  },
  {
    h2: 'IGTV vs Instagram Video Now',
    table: {
      headers: ['', 'Legacy IGTV', 'Instagram Video (2021+)'],
      rows: [
        ['Separate IGTV tab/app', 'Yes (historical)', 'No—merged into main app'],
        ['Typical URL', '/tv/ shortcode', 'Feed post, Reel, or profile video'],
        ['Best SnapInsta page', 'This IGTV downloader when link works', 'Reels or home downloader'],
        ['Upload new “IGTV” today', 'No', 'Publish as Reels or feed video instead'],
      ],
    },
    paragraphs: [
      'For background on the rebrand, read <a href="/what-happened-to-instagram-igtv/">what happened to Instagram IGTV</a>. SnapInsta is not affiliated with Meta or Instagram.',
    ],
  },
  {
    h2: 'Migrating Your Old IGTV Library',
    paragraphs: [
      'Creators who built catalogs on IGTV should audit profile video today and update external links in newsletters or websites.',
      'When you find a working /tv/ link, download a master copy and store it with title, date, and creator credit in your asset manager.',
      'If the link is dead, search the creator’s Reels tab or YouTube cross-posts—many long videos were re-uploaded after the merge.',
    ],
  },
  {
    h2: 'What Meta Changed in 2021',
    paragraphs: [
      'Meta’s announcement described combining IGTV and feed video so creators publish in one place instead of maintaining a separate IGTV channel.',
      'The standalone IGTV app was removed from stores; existing uploads were transitioned where possible. Terminology in the main app shifted toward “video” rather than “IGTV.”',
      'Search engines still surface old “IGTV download” queries—that is why this legacy page remains useful even though new uploads should not be labeled IGTV.',
    ],
  },
  {
    h2: 'Using SnapInsta for Legacy Video Links',
    paragraphs: [
      'Paste the full URL including the shortcode after /tv/. Shortened links from old social posts should be expanded in the browser before copying.',
      'If download succeeds, store the file with a text note describing the original title and creator. Legacy files are easy to mislabel months later.',
      'When download fails, try opening the same URL while logged into Instagram in another tab. If it fails there too, archiving tools cannot recover the media.',
    ],
  },
  {
    h2: 'Planning for Long-Form Video Today',
    paragraphs: [
      'New long videos belong in feed posts or Reels—not revived IGTV branding. Update marketing pages that still say “watch our IGTV series.”',
      'If you depend on old /tv/ links in course materials, schedule a yearly link audit and replace dead URLs with current Instagram posts.',
      'Combine this downloader with your own cloud backup policy so one broken redirect does not erase years of work.',
    ],
  },
  {
    h2: 'FAQ-Style Notes Without the Hype',
    paragraphs: [
      'No downloader can promise every historical IGTV file. Treat success as lucky preservation, not guaranteed access.',
      'If you are a viewer, respect paywalled or private accounts—this tool is for URLs that are already public on the open web.',
    ],
  },
  {
    h2: 'Teaching and Documentation Use Cases',
    paragraphs: [
      'Educators sometimes embed old lecture links that still point to /tv/. Test those links each semester and replace broken ones with current Instagram posts.',
      'When you find a working legacy file, download once and host it in your LMS with proper attribution rather than hotlinking Instagram indefinitely.',
      'Students researching platform history can cite Meta’s 2021 announcement alongside the broken links they discover—real examples make the IGTV transition concrete.',
    ],
  },
  {
    h2: 'When to Stop Trying Legacy URLs',
    paragraphs: [
      'If the /tv/ link fails in a logged-in browser tab and in SnapInsta, assume the media is gone and search for a re-upload.',
      'Spending hours on dead links is less productive than asking the creator for a current post or alternate platform.',
    ],
  },
];
